import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Mailbox, Search, ScanLine, ShieldAlert, CheckCircle2, Clock,
  DollarSign, Layers, Download, Upload, History, AlertOctagon,
} from "lucide-react";

const MAIL_ROLES = new Set(["admin", "mail_clerk"]);

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso.slice(0, 10); }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
function statusPill(sub) {
  if (!sub) return null;
  const map = {
    active: ["bg-green-50 text-green-700 border-green-200", "Active"],
    expiring_soon: ["bg-amber-50 text-amber-700 border-amber-200", "Expiring soon"],
    expired: ["bg-red-50 text-red-700 border-red-200", "Expired"],
    inactive: ["bg-slate-50 text-slate-600 border-slate-200", "No subscription"],
  };
  const [cls, label] = map[sub.subscription_status] || map.inactive;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border ${cls}`} data-testid="mail-sub-status">
      {label}{sub.days_left != null ? ` · ${sub.days_left}d` : ""}
    </span>
  );
}

export default function MailPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const scanRef = useRef(null);

  const [scanValue, setScanValue] = useState("");
  const [lookupBy, setLookupBy] = useState("ref"); // ref | id | name
  const [nameCity, setNameCity] = useState({ name: "", city: "" });
  const [subscriber, setSubscriber] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const [form, setForm] = useState({ check_number: "", amount: "", term_months: "12", note: "" });
  const [busy, setBusy] = useState(false);
  const [confirmDup, setConfirmDup] = useState(null); // {detail} when duplicate check# blocked

  const [batchMode, setBatchMode] = useState(false);
  const [batch, setBatch] = useState(null); // {id,label,count,total_amount,status}
  const [batchLabel, setBatchLabel] = useState("");

  const [ocrStatus, setOcrStatus] = useState({ enabled: false });
  const [ocrResult, setOcrResult] = useState(null);

  // route guard
  useEffect(() => {
    if (loading) return;
    if (!user || typeof user !== "object") { navigate("/login"); return; }
    if (!MAIL_ROLES.has(user.role)) { navigate("/"); return; }
    api.get("/mail/ocr/status").then(r => setOcrStatus(r.data)).catch(() => {});
  }, [loading, user, navigate]);

  // Always-focused scan input
  useEffect(() => {
    const focus = () => {
      if (scanRef.current && !document.activeElement?.closest?.("[data-mail-form]") && !document.activeElement?.closest?.("[data-mail-modal]")) {
        scanRef.current.focus();
      }
    };
    focus();
    window.addEventListener("click", focus);
    const t = setInterval(focus, 3000);
    return () => { window.removeEventListener("click", focus); clearInterval(t); };
  }, [subscriber]);

  const doLookup = async (override) => {
    const payload = {};
    if (override?.business_id) payload.business_id = override.business_id;
    else if (lookupBy === "ref") payload.ref = scanValue.trim();
    else if (lookupBy === "id") payload.business_id = scanValue.trim();
    else { payload.name = nameCity.name.trim(); payload.city = nameCity.city.trim(); }
    if (!payload.ref && !payload.business_id && !payload.name) {
      toast.error("Enter a reference number, ID, or name");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/mail/lookup", payload);
      setSubscriber(data);
      setForm(f => ({ ...f, term_months: String(data.renewal_term_months || 12) }));
      setScanValue("");
      setConfirmDup(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Lookup failed");
    } finally {
      setBusy(false);
    }
  };

  const onScanSubmit = (e) => {
    e.preventDefault();
    if (!scanValue.trim()) return;
    doLookup();
  };

  const openBatch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mail/batch/start", { label: batchLabel });
      setBatch(data);
      setBatchLabel("");
      toast.success(`Batch "${data.label}" opened`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const closeBatch = async () => {
    if (!batch) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/mail/batch/close/${batch.id}`);
      // Download CSV
      const blob = new Blob([data.csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `batch_${data.id}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Batch closed · ${data.count} items · $${data.total_amount.toFixed(2)}`);
      setBatch(null);
      setBatchMode(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitRenewal = async (force = false) => {
    if (!subscriber) return;
    if (!form.check_number.trim()) { toast.error("Check number required"); return; }
    if (!form.amount || Number(form.amount) < 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const body = {
        business_id: subscriber.id,
        check_number: form.check_number.trim(),
        amount: Number(form.amount),
        term_months: Number(form.term_months),
        note: form.note,
        force,
      };
      if (batchMode && batch) body.batch_id = batch.id;
      const { data } = await api.post("/mail/renewal", body);
      toast.success(`Renewed · new expiry ${fmtDate(data.business.subscription_expires_at)}`);
      setSubscriber(data.business);
      setForm({ check_number: "", amount: "", term_months: String(data.business.renewal_term_months || 12), note: "" });
      setConfirmDup(null);
      if (batchMode && batch) {
        setBatch(b => b ? { ...b, count: (b.count || 0) + 1, total_amount: (b.total_amount || 0) + Number(body.amount) } : b);
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      if (status === 409 && !force) {
        setConfirmDup({ detail });
      } else if (status === 403 && detail?.toLowerCase().includes("admin")) {
        toast.error(detail);
      } else {
        toast.error(detail || "Renewal failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const openHistory = async () => {
    if (!subscriber) return;
    try {
      const { data } = await api.get(`/mail/history/${subscriber.id}`);
      setHistory(data);
      setHistoryOpen(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const onOcrFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/mail/ocr/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOcrResult(data);
      if (data.parsed?.external_ref) {
        setLookupBy("ref"); setScanValue(data.parsed.external_ref);
      }
      if (data.parsed?.check_number) setForm(f => ({ ...f, check_number: data.parsed.check_number }));
      if (data.parsed?.amount) setForm(f => ({ ...f, amount: String(data.parsed.amount) }));
      toast.success("OCR parsed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "OCR failed");
    }
  };

  const isAdmin = user && user.role === "admin";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="mail-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-medium mb-2 flex items-center gap-2">
            <Mailbox className="h-3.5 w-3.5" /> Mail Processing Center
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Scan & process renewals</h1>
          <p className="text-sm text-slate-600 mt-2">Scan a reference number to look up a subscriber, then record the check.</p>
        </div>
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3">
          <Layers className="h-5 w-5 text-blue-700" />
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700">Batch mode</span>
            <Switch checked={batchMode} onCheckedChange={setBatchMode} data-testid="mail-batch-toggle" />
          </div>
        </div>
      </div>

      {/* Batch bar */}
      {batchMode && (
        <div className="mb-6 border border-blue-200 bg-blue-50/60 rounded-xl p-4" data-mail-form="batch">
          {!batch ? (
            <div className="flex flex-wrap items-center gap-3">
              <Input
                placeholder="Batch label (optional)"
                value={batchLabel}
                onChange={e => setBatchLabel(e.target.value)}
                className="max-w-sm bg-white"
                data-testid="batch-label-input"
              />
              <Button onClick={openBatch} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="batch-open-btn">
                Open batch
              </Button>
              <span className="text-xs text-slate-600">Open a batch to tally items and download a CSV receipt on close.</span>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Batch</div>
                <div className="font-semibold text-slate-900" data-testid="batch-label">{batch.label}</div>
              </div>
              <div className="flex gap-6">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Items</div>
                  <div className="text-2xl font-bold text-blue-700" data-testid="batch-count">{batch.count || 0}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Total</div>
                  <div className="text-2xl font-bold text-blue-700" data-testid="batch-total">${(batch.total_amount || 0).toFixed(2)}</div>
                </div>
              </div>
              <div className="ml-auto">
                <Button variant="outline" onClick={closeBatch} disabled={busy} data-testid="batch-close-btn">
                  <Download className="h-4 w-4 mr-2" /> Close batch & download CSV
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        {/* LEFT — scan / lookup */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <ScanLine className="h-4 w-4 text-blue-700" />
              <div className="text-sm font-semibold text-slate-900">Scan / lookup</div>
            </div>
            <div className="flex gap-2 mb-3">
              {[["ref","Reference"],["id","Business ID"],["name","Name + city"]].map(([v,l])=>(
                <button
                  key={v}
                  onClick={()=>setLookupBy(v)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${lookupBy===v?"bg-blue-700 text-white border-blue-700":"bg-white text-slate-700 border-slate-200 hover:border-blue-300"}`}
                  data-testid={`lookup-mode-${v}`}
                >{l}</button>
              ))}
            </div>
            {lookupBy !== "name" ? (
              <form onSubmit={onScanSubmit}>
                <Input
                  ref={scanRef}
                  autoFocus
                  value={scanValue}
                  onChange={e=>setScanValue(e.target.value)}
                  placeholder={lookupBy==="ref"?"Scan or type reference #":"Paste business ID"}
                  className="text-lg h-12"
                  data-testid="mail-scan-input"
                />
                <div className="text-xs text-slate-500 mt-2">Press Enter to look up. USB/QR scanners auto-submit.</div>
              </form>
            ) : (
              <div className="space-y-2" data-mail-form="name-lookup">
                <Input placeholder="Business name" value={nameCity.name} onChange={e=>setNameCity({...nameCity, name:e.target.value})} data-testid="lookup-name" />
                <Input placeholder="City (optional)" value={nameCity.city} onChange={e=>setNameCity({...nameCity, city:e.target.value})} data-testid="lookup-city" />
                <Button onClick={()=>doLookup()} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="lookup-name-btn">
                  <Search className="h-4 w-4 mr-2" /> Look up
                </Button>
              </div>
            )}
          </div>

          {/* OCR dropzone (feature-flagged) */}
          <div className={`bg-white border rounded-xl p-5 ${ocrStatus.enabled?"border-slate-200":"border-dashed border-slate-300 opacity-70"}`} data-mail-form="ocr">
            <div className="flex items-center gap-2 mb-2">
              <Upload className="h-4 w-4 text-blue-700" />
              <div className="text-sm font-semibold text-slate-900">OCR (BYO adapter)</div>
              {ocrStatus.enabled
                ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">Enabled</span>
                : <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">Disabled</span>}
            </div>
            <p className="text-xs text-slate-500 mb-3">Drop a check/slip image. Adapter returns placeholder fields until a vendor is connected.</p>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={onOcrFile}
              disabled={!ocrStatus.enabled}
              data-testid="ocr-file-input"
              className="text-xs"
            />
            {ocrResult && (
              <div className="mt-3 text-xs bg-slate-50 border border-slate-200 rounded p-2 font-mono text-slate-700" data-testid="ocr-result">
                {JSON.stringify(ocrResult.parsed, null, 2)}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — subscriber + renewal */}
        <div className="lg:col-span-3">
          {!subscriber ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
              <Mailbox className="h-10 w-10 text-slate-400 mx-auto mb-3" />
              <div className="text-slate-600">Scan or look up a listing to begin.</div>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="subscriber-card">
              <div className="p-5 border-b border-slate-200 bg-slate-50/60">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Subscriber</div>
                    <div className="text-xl font-semibold text-slate-900" data-testid="subscriber-name">{subscriber.name}</div>
                    <div className="text-sm text-slate-600">{subscriber.category} · {subscriber.city}, {subscriber.state}</div>
                    {subscriber.external_ref && (
                      <div className="text-xs text-slate-500 mt-1">Ref: <b className="font-mono">{subscriber.external_ref}</b></div>
                    )}
                  </div>
                  <div className="text-right space-y-2">
                    {statusPill(subscriber)}
                    <div className="text-xs text-slate-500">Expires <b>{fmtDate(subscriber.subscription_expires_at)}</b></div>
                    <div className="text-xs text-slate-500">Last check <b className="font-mono">{subscriber.last_check_number || "—"}</b></div>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" onClick={openHistory} data-testid="mail-history-btn">
                    <History className="h-3.5 w-3.5 mr-1" /> History
                  </Button>
                  <Button size="sm" variant="ghost" onClick={()=>setSubscriber(null)} data-testid="mail-clear-btn">
                    Clear
                  </Button>
                </div>
              </div>

              <div className="p-5" data-mail-form="renewal">
                <div className="flex items-center gap-2 mb-4">
                  <DollarSign className="h-4 w-4 text-blue-700" />
                  <div className="text-sm font-semibold text-slate-900">Record renewal payment</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1">Check #</label>
                    <Input value={form.check_number} onChange={e=>setForm({...form,check_number:e.target.value})} placeholder="e.g. 10245" data-testid="renewal-check" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1">Amount ($)</label>
                    <Input type="number" step="0.01" min="0" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} placeholder="99.00" data-testid="renewal-amount" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1">Term</label>
                    <Select value={form.term_months} onValueChange={v=>setForm({...form,term_months:v})}>
                      <SelectTrigger data-testid="renewal-term"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="12">1 year (12 mo)</SelectItem>
                        <SelectItem value="24">2 years (24 mo)</SelectItem>
                        <SelectItem value="36">3 years (36 mo)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1">Note (optional)</label>
                    <Input value={form.note} onChange={e=>setForm({...form,note:e.target.value})} placeholder="—" data-testid="renewal-note" />
                  </div>
                </div>

                {confirmDup && (
                  <div className="mt-4 border border-amber-300 bg-amber-50 rounded-lg p-3 text-sm text-amber-900" data-testid="duplicate-warning">
                    <div className="flex items-start gap-2">
                      <AlertOctagon className="h-4 w-4 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium">Duplicate check detected</div>
                        <div className="text-xs mt-1">{confirmDup.detail}</div>
                        {isAdmin ? (
                          <div className="mt-2 flex gap-2">
                            <Button size="sm" variant="outline" onClick={()=>setConfirmDup(null)} data-testid="dup-cancel">Cancel</Button>
                            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={()=>submitRenewal(true)} data-testid="dup-override">
                              Override & record
                            </Button>
                          </div>
                        ) : (
                          <div className="mt-2 text-xs italic flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5" /> Admin approval required to override.</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-5 flex gap-2">
                  <Button
                    onClick={()=>submitRenewal(false)}
                    disabled={busy}
                    className="bg-blue-700 hover:bg-blue-800"
                    data-testid="renewal-submit-btn"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Record renewal
                  </Button>
                  {batchMode && !batch && <span className="text-xs text-amber-700 self-center">Open a batch to add this item to a tally.</span>}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* History modal (simple) */}
      {historyOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-mail-modal onClick={()=>setHistoryOpen(false)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[80vh] overflow-hidden shadow-xl" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-700" />
                <div className="font-semibold text-slate-900">Renewal history</div>
              </div>
              <button onClick={()=>setHistoryOpen(false)} className="text-slate-500 hover:text-slate-900 text-sm">Close</button>
            </div>
            <div className="max-h-[65vh] overflow-auto p-4">
              {history.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-10">No prior payments recorded.</div>
              ) : (
                <table className="w-full text-sm" data-testid="mail-history-table">
                  <thead className="text-xs uppercase tracking-widest text-slate-500">
                    <tr className="border-b border-slate-200"><th className="text-left py-2">When</th><th className="text-left">Check #</th><th className="text-left">Amount</th><th className="text-left">Term</th><th className="text-left">New expiry</th><th className="text-left">By</th></tr>
                  </thead>
                  <tbody>
                    {history.map(h=>(
                      <tr key={h.id} className="border-b border-slate-100">
                        <td className="py-2 text-slate-700">{fmtDateTime(h.processed_at)}</td>
                        <td className="font-mono text-slate-700">{h.check_number}{h.override && <span className="ml-1 text-[10px] text-amber-700">(override)</span>}</td>
                        <td className="text-slate-700">${Number(h.amount||0).toFixed(2)}</td>
                        <td className="text-slate-700">{h.term_months} mo</td>
                        <td className="text-slate-700">{fmtDate(h.new_expiry)}</td>
                        <td className="text-slate-500 text-xs">{h.processed_by_email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
